import { useCallback, useEffect, useState } from 'react';
import {
  CustomerJourneyState,
  NamiCustomerManager,
} from '@namiml/web-sdk';
import ErrorMessage from '../components/ErrorMessage';
import EditableButton from '../components/EditableButton';

const Dot = ({ active }: { active: boolean }) => (
  <span
    style={{
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: active ? '#3ce7a8' : '#1f2b3a',
      border: active ? '1px solid rgba(60, 231, 168, 0.8)' : '1px solid rgba(120, 133, 156, 0.6)',
      display: 'inline-block',
      marginRight: 8,
    }}
  />
);

const ProfilePage = () => {
  const [journeyState, setJourneyState] = useState<CustomerJourneyState | null>(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [loginId, setLoginId] = useState('TEST_LOGIN_ID');
  const [externalId, setExternalId] = useState<string | undefined>();
  const [deviceId, setDeviceId] = useState('');
  const [error, setError] = useState('');

  const handleLogin = useCallback(() => {
    try {
      NamiCustomerManager.login(loginId);
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error logging in: ${details}`);
    }
  }, [loginId]);

  const handleLogout = useCallback(() => {
    try {
      NamiCustomerManager.logout();
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error logging out: ${details}`);
    }
  }, []);

  const refreshJourneyState = useCallback(() => {
    try {
      setJourneyState(NamiCustomerManager.journeyState());
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error getting journey state: ${details}`);
    }
  }, []);

  const checkLoginStatus = useCallback(() => {
    try {
      setIsUserLoggedIn(NamiCustomerManager.isLoggedIn());
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error checking login state: ${details}`);
    }
  }, []);

  const refreshIds = useCallback(() => {
    try {
      setExternalId(NamiCustomerManager.loggedInId() ?? undefined);
      setDeviceId(NamiCustomerManager.deviceId() ?? '');
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error fetching IDs: ${details}`);
    }
  }, []);

  useEffect(() => {
    checkLoginStatus();
    refreshJourneyState();
    refreshIds();

    const unsubscribeJourney = NamiCustomerManager.registerJourneyStateHandler((state) => {
      setJourneyState(state);
    });

    const unsubscribeAccount = NamiCustomerManager.registerAccountStateHandler((action, success, accountError) => {
      if (accountError) {
        console.error(accountError);
        setError(`Account action failed: ${accountError}`);
        return;
      }
      if (action === 'login') {
        setIsUserLoggedIn(Boolean(success));
      }
      if (action === 'logout') {
        setIsUserLoggedIn(!Boolean(success));
      }
      refreshIds();
    });

    return () => {
      unsubscribeJourney?.();
      unsubscribeAccount?.();
    };
  }, [checkLoginStatus, refreshIds, refreshJourneyState]);

  return (
    <div className="screen-root">
      <div className="panel stack">
        <h1 className="panel-title">Profile</h1>
        <p className="panel-subtitle">Manage customer state and inspect journey progress.</p>
        {error && <ErrorMessage message={error} />}

        <div className="panel">
          <h2 className="panel-title">Login</h2>
          <div className="option-grid">
            <EditableButton
              label="Login ID"
              value={loginId}
              onSubmit={setLoginId}
              additionalStyles={{ flex: 1 }}
            />
            <button
              type="button"
              className="tv-button"
              onClick={isUserLoggedIn ? handleLogout : handleLogin}
            >
              {isUserLoggedIn ? 'Logout' : 'Login'}
            </button>
          </div>
        </div>

        <div className="panel">
          <h2 className="panel-title">Account Identifiers</h2>
          <div className="option-grid">
            <EditableButton
              label={isUserLoggedIn ? 'External ID' : 'Device ID'}
              value={isUserLoggedIn ? externalId : deviceId}
              onSubmit={(value) => {
                if (isUserLoggedIn) {
                  setExternalId(value);
                } else {
                  setDeviceId(value);
                }
              }}
              placeholder="Unavailable"
              additionalStyles={{ flex: 1 }}
            />
            <div className="notice">
              <div className="panel-subtitle">Current Mode</div>
              <div className="panel-title" style={{ fontSize: '1.1rem' }}>
                {isUserLoggedIn ? 'Registered User' : 'Anonymous User'}
              </div>
            </div>
          </div>
        </div>

        <div className="panel">
          <h2 className="panel-title">Journey State</h2>
          <div className="stack">
            {journeyState ? (
              Object.entries(journeyState).map(([key, value]) => (
                <div key={key} className="list-row">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Dot active={Boolean(value)} />
                    <span>{key.replace(/_/g, ' ').toUpperCase()}</span>
                  </div>
                  <span style={{ textAlign: 'right', fontWeight: 600 }}>
                    {value ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              ))
            ) : (
              <div className="panel-subtitle">No journey state available.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
